<script setup lang="ts">
import { computed, onBeforeUnmount, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import { AlarmClock, AlarmClockOff, Grid2X2, Palette, Square, SquareCheck } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { DynamicModalType, Target } from '@/types'
import { useDynamicModalStore } from '@/stores/dynamicModalStore'
import { useSearchStore } from '@/stores/searchStoreV2'
import { useAutoScroll } from '@/composables/useAutoScroll'
import { useGraphicStyles } from '@/composables/useGraphicStyles'
import { useImageStore } from '@/stores/imageStore'
import { useAuthStore } from '@/stores/authStore'

const { toggleAutoScrollSpeed, speed } = useAutoScroll()

const imageStore = useImageStore()
const searchStore = useSearchStore()
const { t } = useI18n()
const { selectedStyles } = storeToRefs(searchStore)
const { userGalleryProfile, gridMode } = storeToRefs(imageStore)
const dynamicModalStore = useDynamicModalStore()
const route = useRoute()

const showSearchBtn = shallowRef<boolean>(false)

showSearchBtn.value = route.path === '/explore' || route.path === '/collection' || route.path === '/creations' || route.path.includes('/gallery')
watch(
	() => route.fullPath,
	() => {
		showSearchBtn.value = route.path === '/explore' || route.path === '/collection' || route.path === '/creations' || route.path.includes('/gallery')
	},
)

function URLChange() {
	dynamicModalStore.closeModal()
}

onBeforeUnmount(() => {
	window.removeEventListener('popstate', URLChange)
})

const styles = computed(() => {
	const sel = selectedStyles.value.length

	return `${sel}/${useGraphicStyles().getStylePipeline().length}`
})

window.addEventListener('popstate', URLChange)
</script>

<template>
	<div
		v-if="useAuthStore().isAuth()"
		class="z-[7] container-bottom-menu pointer-events-none"
	>
		<router-link
			v-if="route.name === Target.UserGallery"
			:to="`/profile/${userGalleryProfile.username}-${userGalleryProfile.id}`"
			class="bg-slate-800 italic text-sm z-[7] no-underline button-purple-shade button-shadow  pointer-events-auto"
		>
			{{ userGalleryProfile.username }}
		</router-link>
		<div class="flex flex-nowrap items-center justify-center gap-2">
			<Button
				v-if="showSearchBtn"
				class="button-orange-shade pointer-events-auto button-shadow !hidden sm:!flex"
				:class="{
					'!bg-none !bg-opacity-50 backdrop-blur-sm bg-orange-600': !gridMode,
				}"
				@click="gridMode = !gridMode"
			>
				<template #default>
					<div class="flex-center gap-1">
						<SquareCheck
							v-if="gridMode"
							class="lucide-button-icon"
						/>
						<Square
							v-else
							class="lucide-button-icon"
						/>
						<span class="text-xsp">{{ t('common.grid') }}</span>
					</div>
				</template>
			</Button>
			<Button
				v-if="showSearchBtn"
				v-tooltip.top="{ value: t('tooltips.autoscroll'),
					showDelay: 400,
					disabled: speed > 0,
					pt: { text: {
						class: 'w-fit whitespace-nowrap' },
					},
				}"
				class="min-w-[80px] button-orange-shade button-shadow pointer-events-auto"

				@click="toggleAutoScrollSpeed"
			>
				<template #default>
					<div class="flex-center gap-1">
						<AlarmClock
							v-if="speed > 0"
							class="lucide-button-icon-sm"
						/>
						<AlarmClockOff
							v-else
							class="lucide-button-icon-sm"
						/>
						<span
							v-if="speed > 0"
							class="text-xsp"
						> {{ speed }}</span>
						<span
							v-else
							class="text-xsp"
						>
							Off
						</span>
					</div>
				</template>
			</Button>
			<Button
				v-if="showSearchBtn"
				class="button-orange-shade button-shadow pointer-events-auto"
				@click="dynamicModalStore.showModal(DynamicModalType.SelectFilterStyles, {
					props: {
						mode: 'filter',
					},
				})"
			>
				<template #default>
					<div class="flex-center gap-1">
						<Palette class="lucide-button-icon-sm" />
						<span class="text-xsp"> {{ styles }}</span>
					</div>
				</template>
			</Button>
		</div>
	</div>
</template>

<style>

</style>

<style scoped lang="scss">
.router-link-active .button-purple-shade  {
    @apply bg-gradient-to-r from-purple-500 to-purple-600  hover:to-purple-700 hover:bg-gradient-to-br
}

.container-bottom-menu {
	@apply w-fit bottom-3 flex flex-col items-end gap-2 fixed md:right-6 right-1/2 translate-x-1/2 md:translate-x-0;
}
</style>
